/**
 * Health goals — list + create.
 */

import { useState, type FormEvent } from 'react';
import {
  useHealthGoals,
  useCreateHealthGoal,
  type HealthGoalDto,
  type HealthGoalType,
} from '../../api/health';
import styles from './health.module.css';

const GOAL_TYPE_LABELS: Record<HealthGoalType, string> = {
  metric_target: 'Metric target',
  workout_volume: 'Training volume',
  new_capability: 'New capability',
  performance: 'Performance PR',
  habit: 'Habit / consistency',
};

export function HealthGoalsScreen() {
  const goals = useHealthGoals();
  const create = useCreateHealthGoal();
  const [showForm, setShowForm] = useState(false);

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h2 className={styles.title}>Goals</h2>
          <div className={styles.subtitle}>
            What you're aiming for. Each goal has a type that decides how
            "baseline → current → target" reads (kg, time, capability, …).
          </div>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className="kos-btn kos-btn-primary"
            onClick={() => setShowForm((s) => !s)}
          >
            {showForm ? 'Cancel' : 'New goal'}
          </button>
        </div>
      </header>

      {showForm && (
        <NewGoalForm
          onCreated={() => {
            setShowForm(false);
          }}
          onSubmit={(input) => create.mutate(input)}
          pending={create.isPending}
        />
      )}

      {goals.isLoading ? (
        <div className={styles.muted}>Loading…</div>
      ) : (goals.data?.goals ?? []).length === 0 ? (
        <div className={styles.empty}>
          No goals yet. Create one with the button above.
        </div>
      ) : (
        <div className={styles.list}>
          {(goals.data?.goals ?? []).map((g) => (
            <GoalRow key={g.id} goal={g} />
          ))}
        </div>
      )}
    </div>
  );
}

function GoalRow({ goal }: { goal: HealthGoalDto }) {
  return (
    <div className={styles.row}>
      <div className={styles.rowMain}>
        <div className={styles.rowTitle}>{goal.title}</div>
        <div className={styles.rowMeta}>
          {GOAL_TYPE_LABELS[goal.type]}
          {goal.metric ? ` · ${goal.metric}` : ''}
          {goal.targetDate ? ` · by ${goal.targetDate}` : ''}
          {' · '}
          baseline {fmt(goal.baseline)}
          {' → '}
          current {fmt(goal.current)}
          {' → '}
          target {fmt(goal.target)}
          {goal.unit ? ` ${goal.unit}` : ''}
        </div>
      </div>
      <span className={styles.tag}>{goal.status}</span>
    </div>
  );
}

function fmt(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function NewGoalForm({
  onSubmit,
  pending,
  onCreated,
}: {
  onSubmit: (input: { type: HealthGoalType; title: string; metric?: string | null; unit?: string | null; baseline?: unknown; target?: unknown; current?: unknown; targetDate?: string | null; notes?: string }) => void;
  pending: boolean;
  onCreated: () => void;
}) {
  const [form, setForm] = useState<{
    type: HealthGoalType;
    title: string;
    metric: string;
    unit: string;
    baseline: string;
    target: string;
    targetDate: string;
    notes: string;
  }>({
    type: 'metric_target',
    title: '',
    metric: '',
    unit: '',
    baseline: '',
    target: '',
    targetDate: '',
    notes: '',
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      type: form.type,
      title: form.title.trim(),
      metric: form.metric.trim() || null,
      unit: form.unit.trim() || null,
      baseline: form.baseline.trim() ? maybeNumber(form.baseline) : null,
      target: form.target.trim() ? maybeNumber(form.target) : null,
      targetDate: form.targetDate || null,
      notes: form.notes,
    });
    setForm({
      type: 'metric_target',
      title: '',
      metric: '',
      unit: '',
      baseline: '',
      target: '',
      targetDate: '',
      notes: '',
    });
    onCreated();
  }

  return (
    <form className={styles.card} onSubmit={handleSubmit} style={{ marginBottom: 18 }}>
      <div className={styles.formGrid}>
        <label className={styles.label}>Type</label>
        <select
          className={styles.select}
          value={form.type}
          onChange={(e) =>
            setForm((f) => ({ ...f, type: e.target.value as HealthGoalType }))
          }
        >
          {Object.entries(GOAL_TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>

        <label className={styles.label}>Title</label>
        <input
          className={styles.input}
          required
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          placeholder="e.g. Drop to 80 kg"
        />

        <label className={styles.label}>Metric</label>
        <input
          className={styles.input}
          value={form.metric}
          onChange={(e) => setForm((f) => ({ ...f, metric: e.target.value }))}
          placeholder="weight, squat_5rm, 10km_time, …"
        />

        <label className={styles.label}>Unit</label>
        <input
          className={styles.input}
          value={form.unit}
          onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
          placeholder="kg, min, reps, …"
        />

        <label className={styles.label}>Baseline</label>
        <input
          className={styles.input}
          value={form.baseline}
          onChange={(e) => setForm((f) => ({ ...f, baseline: e.target.value }))}
        />

        <label className={styles.label}>Target</label>
        <input
          className={styles.input}
          value={form.target}
          onChange={(e) => setForm((f) => ({ ...f, target: e.target.value }))}
        />

        <label className={styles.label}>Target date</label>
        <input
          className={styles.input}
          type="date"
          value={form.targetDate}
          onChange={(e) => setForm((f) => ({ ...f, targetDate: e.target.value }))}
        />

        <label className={styles.label}>Notes</label>
        <textarea
          className={styles.textarea}
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
        />
      </div>
      <div className={styles.formFoot}>
        <button type="submit" className="kos-btn kos-btn-primary" disabled={pending}>
          {pending ? 'Creating…' : 'Create goal'}
        </button>
      </div>
    </form>
  );
}

function maybeNumber(s: string): unknown {
  const n = Number(s);
  return Number.isFinite(n) && s.trim() !== '' ? n : s;
}
